/**
 * Physically-based lighting for glass UI surfaces.
 *
 * Scene
 *   Light   point light at (Lx, Ly, Lz), static in world space
 *   Viewer  point eye at (W/2, H/2, Ez), static
 *   Surface each glass pane, centred at C, with normal N
 *
 * Why the surfaces are curved
 * ---------------------------
 * A perfectly FLAT page under a static light has one mirror point for the whole
 * document, fixed in viewport space. Panes slide beneath it but the highlight
 * never travels across the screen, which reads as lights pinned to the glass —
 * correct, and visually dead.
 *
 * Real cover glass is not flat. It curves, so tilting it sweeps the highlight
 * along the surface. We model the page as a gently curved sheet: a pane's normal
 * tilts with its position in the viewport. Each pane therefore has its OWN
 * mirror point, and as it scrolls its tilt changes, so the highlight travels
 * across it. The motion is a consequence of the geometry, not an animation
 * bolted on top.
 *
 * Per-surface solve
 *   N   = normalise(sin(tiltX), sin(tiltY), 1)
 *   L'  = L − 2((L−C)·N)N                 light mirrored through the pane
 *   P   = L' + t(E − L'),  t = d / ((E−L')·N),  d = (L−C)·N
 * P is where a mirror at this orientation would show the light. Diffuse and
 * Fresnel use the same N, so a pane's shading is self-consistent.
 *
 * Motion damping
 *   Targets are chased with a per-frame lerp rather than written directly, so
 *   highlights trail and settle instead of snapping to scroll position. The rAF
 *   loop keeps running while anything is still in motion and stops once
 *   everything is within epsilon, so an idle page costs nothing.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.hasAttribute('data-mkn-glass')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var tracked = new Set();
  var onScreen = new Set();
  var scene = null;
  var running = false;

  // Curvature at 100% — the steepest tilt applied at the viewport extremes.
  var MAX_TILT = 0.55; // radians, ~31deg
  var EPS = 0.02;

  function num(cs, prop, fallback) {
    var v = parseFloat(cs.getPropertyValue(prop));
    return isNaN(v) ? fallback : v;
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function specularAt(px, py, L, E, shininess) {
    var lx = L.x - px, ly = L.y - py, lz = L.z;
    var ll = Math.hypot(lx, ly, lz) || 1;
    lx /= ll; ly /= ll; lz /= ll;
    var vx = E.x - px, vy = E.y - py, vz = E.z;
    var vl = Math.hypot(vx, vy, vz) || 1;
    vx /= vl; vy /= vl; vz /= vl;
    var hx = lx + vx, hy = ly + vy, hz = lz + vz;
    var hl = Math.hypot(hx, hy, hz) || 1;
    var ndh = hz / hl;
    return ndh <= 0 ? 0 : Math.pow(ndh, shininess);
  }

  function solveScene() {
    var cs = getComputedStyle(root);
    var W = window.innerWidth;
    var H = window.innerHeight;

    var L = {
      x: (W * num(cs, '--mkn-light-x', 50)) / 100,
      y: (H * num(cs, '--mkn-light-y', 15)) / 100,
      z: (H * num(cs, '--mkn-light-height', 60)) / 100
    };
    var E = { x: W / 2, y: H / 2, z: (W * num(cs, '--mkn-eye-distance', 140)) / 100 };

    var shininess = num(cs, '--mkn-shininess', 24);
    var ior = num(cs, '--mkn-ior', 150) / 100;

    // Lobe size is a scale, not a position — the flat solve is a good enough
    // approximation across the small tilts involved, and this way the bisection
    // runs once per resize instead of once per surface per frame.
    var flatT = L.z / (L.z + E.z);
    var M0 = { x: L.x + flatT * (E.x - L.x), y: L.y + flatT * (E.y - L.y) };
    var peak = specularAt(M0.x, M0.y, L, E, shininess) || 1e-6;
    var lo = 0, hi = Math.max(W, H) * 1.5;
    for (var i = 0; i < 22; i++) {
      var mid = (lo + hi) / 2;
      if (specularAt(M0.x + mid, M0.y, L, E, shininess) > peak * 0.5) lo = mid;
      else hi = mid;
    }

    scene = {
      W: W, H: H, L: L, E: E,
      lobe: Math.max((lo + hi) / 2, 8),
      ior: ior,
      thickness: num(cs, '--mkn-thickness', 22),
      tilt: (num(cs, '--mkn-curvature', 55) / 100) * MAX_TILT,
      // Smoothing -> per-frame lerp factor. 0% smoothing snaps; 95% crawls.
      lerp: clamp(1 - num(cs, '--mkn-smoothing', 80) / 100, 0.03, 1),
      f0: Math.pow((1 - ior) / (1 + ior), 2)
    };
  }

  /** Target shading values for one surface. */
  function solveSurface(r) {
    var L = scene.L, E = scene.E;
    var cx = r.left + r.width / 2;
    var cy = r.top + r.height / 2;

    // --- Surface normal from page curvature -------------------------------
    var tx = ((cx - scene.W / 2) / scene.W) * scene.tilt;
    var ty = ((cy - scene.H / 2) / scene.H) * scene.tilt;
    var nx = Math.sin(tx), ny = Math.sin(ty), nz = 1;
    var nl = Math.hypot(nx, ny, nz) || 1;
    nx /= nl; ny /= nl; nz /= nl;

    // --- Mirror point for THIS pane's orientation -------------------------
    var lcx = L.x - cx, lcy = L.y - cy, lcz = L.z;
    var d = lcx * nx + lcy * ny + lcz * nz;

    var lpx = L.x - 2 * d * nx, lpy = L.y - 2 * d * ny, lpz = L.z - 2 * d * nz;
    var ex = E.x - lpx, ey = E.y - lpy, ez = E.z - lpz;
    var den = ex * nx + ey * ny + ez * nz;

    var px, py;
    if (Math.abs(den) < 1e-6) {
      px = cx; py = cy; // light grazing the pane — degenerate, park at centre
    } else {
      var t = d / den;
      px = lpx + t * ex;
      py = lpy + t * ey;
    }

    // --- Lambert diffuse, inverse-square, using the same normal -----------
    var dist = Math.hypot(lcx, lcy, lcz) || 1;
    var ndl = Math.max(0, (lcx * nx + lcy * ny + lcz * nz) / dist);
    var diffuse = clamp((ndl / (dist * dist)) * L.z * L.z, 0, 1);

    // --- Schlick-Fresnel, same normal -------------------------------------
    var vx = E.x - cx, vy = E.y - cy, vz = E.z;
    var vlen = Math.hypot(vx, vy, vz) || 1;
    var cosT = clamp((vx * nx + vy * ny + vz * nz) / vlen, 0, 1);
    var fres = scene.f0 + (1 - scene.f0) * Math.pow(1 - cosT, 5);
    var fresN = clamp((fres - scene.f0) / (1 - scene.f0), 0, 1);

    // --- Refraction: lateral shift through the slab -----------------------
    var sinT = Math.sqrt(Math.max(0, 1 - cosT * cosT));
    var thetaT = Math.asin(clamp(sinT / scene.ior, -1, 1));
    var shift =
      scene.thickness * (Math.sin(Math.acos(cosT) - thetaT) / Math.max(Math.cos(thetaT), 1e-4));
    var hx = cx - E.x, hy = cy - E.y;
    var hlen = Math.hypot(hx, hy) || 1;

    // --- Where the light catches the LIP ---------------------------------
    // Nearest point of the pane's boundary to the mirror hotspot. Clamping the
    // hotspot to the rect gives exactly that, and as the hotspot travels the
    // clamped point slides continuously around the perimeter.
    var qx = clamp(px, r.left, r.right);
    var qy = clamp(py, r.top, r.bottom);
    var off = Math.hypot(px - qx, py - qy);

    // When the hotspot is ON the pane the reflection belongs to the face, not
    // the edge, so this term is zero there. It rises as the hotspot leaves the
    // pane, then falls away with distance.
    var edgeI = Math.min(1, off / 34) / (1 + Math.pow(off / 340, 2));

    return {
      gx: ((px - r.left) / r.width) * 100,
      gy: ((py - r.top) / r.height) * 100,
      rx: (scene.lobe / r.width) * 100,
      ry: (scene.lobe / r.height) * 100,
      fx: ((px + (hx / hlen) * shift - r.left) / r.width) * 100,
      fy: ((py + (hy / hlen) * shift - r.top) / r.height) * 100,
      diffuse: diffuse,
      fresnel: fresN,
      ex: ((qx - r.left) / r.width) * 100,
      ey: ((qy - r.top) / r.height) * 100,
      ei: edgeI,
      // Bearing to this pane's own hotspot, clockwise from 12 o'clock — the
      // convention conic-gradient uses, so its brightest stop lands on the
      // edge facing the light.
      bearing: ((Math.atan2(px - cx, -(py - cy)) * 180) / Math.PI + 360) % 360
    };
  }

  var KEYS = ['gx', 'gy', 'rx', 'ry', 'fx', 'fy', 'diffuse', 'fresnel', 'ex', 'ey', 'ei'];

  function frame() {
    if (!scene || !onScreen.size) { running = false; return; }

    var k = scene.lerp;
    var pending = [];
    var moving = false;

    // --- read phase: no style writes in here ------------------------------
    onScreen.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var t = solveSurface(r);
      if (el.matches(HEADER_SEL)) lipTarget(t);
      pending.push([el, t]);
    });

    // --- integrate + write ------------------------------------------------
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i][0];
      var target = pending[i][1];
      var cur = el.__mknState;

      if (!cur) {
        cur = el.__mknState = target; // first paint lands exactly
      } else if (reduceMotion) {
        cur = el.__mknState = target;
      } else {
        for (var j = 0; j < KEYS.length; j++) {
          var key = KEYS[j];
          var delta = target[key] - cur[key];
          if (Math.abs(delta) > EPS) moving = true;
          cur[key] += delta * k;
        }
        // Angles wrap: take the short way round or the rim spins the long way
        // through 359 -> 0 and visibly snaps.
        var da = ((target.bearing - cur.bearing + 540) % 360) - 180;
        if (Math.abs(da) > EPS) moving = true;
        cur.bearing = (cur.bearing + da * k + 360) % 360;
      }

      var s = el.style;
      s.setProperty('--mkn-gx', cur.gx.toFixed(2) + '%');
      s.setProperty('--mkn-gy', cur.gy.toFixed(2) + '%');
      s.setProperty('--mkn-rx', cur.rx.toFixed(2) + '%');
      s.setProperty('--mkn-ry', cur.ry.toFixed(2) + '%');
      s.setProperty('--mkn-refract-x', cur.fx.toFixed(2) + '%');
      s.setProperty('--mkn-refract-y', cur.fy.toFixed(2) + '%');
      s.setProperty('--mkn-diffuse', cur.diffuse.toFixed(3));
      s.setProperty('--mkn-fresnel', cur.fresnel.toFixed(3));
      s.setProperty('--mkn-edge-hot-x', cur.ex.toFixed(2) + '%');
      s.setProperty('--mkn-edge-hot-y', cur.ey.toFixed(2) + '%');
      s.setProperty('--mkn-edge-hot', cur.ei.toFixed(3));
      s.setProperty('--mkn-rim-bearing', cur.bearing.toFixed(1) + 'deg');
    }

    // Keep the loop alive only while something is still settling.
    if (moving && !reduceMotion) window.requestAnimationFrame(frame);
    else running = false;
  }

  function kick() {
    if (running) return;
    running = true;
    window.requestAnimationFrame(frame);
  }

  function observe(el) {
    if (tracked.has(el)) return;
    tracked.add(el);
    if (scene && scene.observer) scene.observer.observe(el);
    else onScreen.add(el);
  }

  // Distance of scroll over which the header's refracted light makes one pass
  // along its lip and back.
  var LIP_CYCLE = 750;

  var HEADER_SEL = '.header__underlay-closed, .header__underlay-open';

  /**
   * The header is fixed, so it cannot reflect the fixed light in any way that
   * changes — that is what made its highlight a dead glow. What it CAN refract
   * is light coming off the page moving behind it, and that genuinely moves.
   *
   * So its lip is driven by scroll distance instead of by its own geometry.
   * Ping-ponged rather than wrapped: a modulo would teleport the highlight from
   * one end of the bar to the other every cycle.
   */
  function lipTarget(target) {
    var p = ((window.scrollY || 0) % (LIP_CYCLE * 2)) / LIP_CYCLE; // 0..2
    var tri = p < 1 ? p : 2 - p; // 0..1..0, continuous at the turn
    target.ex = -12 + tri * 124; // overshoot both ends so it leaves the bar
    target.ey = 100; // the bottom lip
    target.ei = 1;
    return target;
  }

  // Horizon owns the header underlay markup, so it cannot carry the class.
  var EXTRA_SURFACES =
    '.header__underlay-closed, .header__underlay-open, ' +
    '.header-actions__action, .header__icon-list a, search-button > button';

  function scan() {
    document.querySelectorAll('.mkn-glass').forEach(observe);
    document.querySelectorAll(EXTRA_SURFACES).forEach(observe);
    kick();
  }

  function init() {
    solveScene();

    if ('IntersectionObserver' in window) {
      scene.observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) onScreen.add(entries[i].target);
            else onScreen.delete(entries[i].target);
          }
          kick();
        },
        { rootMargin: '20% 0px 20% 0px', threshold: 0 }
      );
      tracked.forEach(function (el) { scene.observer.observe(el); });
    }

    scan();

    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener(
      'resize',
      function () {
        var obs = scene && scene.observer;
        solveScene();
        if (obs) {
          scene.observer = obs;
        }
        kick();
      },
      { passive: true }
    );
    document.addEventListener('shopify:section:load', scan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
